import React from 'react';
import { Box, Typography, IconButton, Checkbox } from '@mui/material';
import RGL, { WidthProvider } from 'react-grid-layout';
import { GroupWithId } from './MetadataInterfaces';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveIcon from '@mui/icons-material/OpenWith';

const ReactGridLayout = WidthProvider(RGL);

interface GroupListProps {
  groups: GroupWithId[];
  selectedGroup: GroupWithId | null;
  onGroupClick: (group: GroupWithId) => void;
  onUpdateGroups: (updatedGroups: GroupWithId[]) => void;
  onEditGroup: (group: GroupWithId) => void;
  onDeleteGroup: (groupId: string) => void;
  onMoveGroup: (group: GroupWithId) => void;
  isSelectionMode: boolean;
  selectedItems: string[];
  onSelectionChange: (id: string) => void;
}

const GroupList: React.FC<GroupListProps> = ({
  groups,
  selectedGroup,
  onGroupClick,
  onUpdateGroups,
  onEditGroup,
  onDeleteGroup,
  onMoveGroup,
  isSelectionMode,
  selectedItems,
  onSelectionChange,
}) => {
  const onLayoutChange = (layout: RGL.Layout[]) => {
    const newGroups = layout
      .sort((a, b) => a.y - b.y)
      .map((item) => groups.find((g) => g.id === item.i))
      .filter((group): group is GroupWithId => group !== undefined);

    onUpdateGroups(newGroups);
  };

  const handleMouseDown = (e: React.MouseEvent, group: GroupWithId) => {
    // Check if the click is on the drag handle
    if (!(e.target as HTMLElement).closest('.drag-handle')) {
      e.preventDefault(); // Prevent drag from starting
      onGroupClick(group);
    }
  };

  return (
    <ReactGridLayout
      className="layout"
      layout={groups.map((group, index) => ({
        i: group.id,
        x: 0,
        y: index,
        w: 1,
        h: 1,
      }))}
      cols={1}
      rowHeight={40}
      width={250}
      onLayoutChange={onLayoutChange}
      isResizable={false}
      draggableHandle=".drag-handle"
    >
      {groups.map((group) => (
        <div key={group.id}>
          <Box
            onMouseDown={(e) => handleMouseDown(e, group)}
            sx={{
              p: 1,
              height: '100%',
              cursor: 'pointer',
              bgcolor:
                selectedGroup?.id === group.id
                  ? 'action.selected'
                  : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0, // This allows the text to shrink
                flex: 1,
              }}
            >
              {isSelectionMode && (
                <Checkbox
                  checked={selectedItems.includes(group.id)}
                  onChange={() => onSelectionChange(group.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {group.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <IconButton onClick={() => onMoveGroup(group)} size="small">
                <MoveIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => onEditGroup(group)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => onDeleteGroup(group.id)} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton className="drag-handle" size="small">
                ⋮
              </IconButton>
            </Box>
          </Box>
        </div>
      ))}
    </ReactGridLayout>
  );
};

export default GroupList;
